// frontend/src/pages/StudentFormPage.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form, Input, DatePicker, Alert, Select, Typography, Card, Row, Col, Divider, Checkbox, Spin, Tabs, Space, notification, theme, Upload, Avatar } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, CameraOutlined } from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ImgCrop from 'antd-img-crop';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { getStudentById, createStudent, updateStudent, uploadStudentProfilePicture } from '../services/studentService';
import { getAllClasses } from '../services/classService';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import useResponsive from '../hooks/useResponsive';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const StudentFormPage = () => {
    const { message } = useNotification();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form] = Form.useForm();

    // --- Access Theme Token ---
    const { token } = theme.useToken();

    const { isMobile } = useResponsive();
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in StudentFormPage:", err);
            }
        };
        fetchSettings();
    }, []);

    const [isClassTeacher, setIsClassTeacher] = useState(false);
    const isEditDisabled = user?.role === 'teacher' && settings?.allow_teacher_student_edit === false && (isEditMode ? !isClassTeacher : classes.length === 0);
    const [classes, setClasses] = useState([]);
    const [activeTab, setActiveTab] = useState('1'); // controlled tabs
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [uploadingPicture, setUploadingPicture] = useState(false);

    const isEditMode = !!id;

    // --- START: Field Definitions (using useMemo for stability) ---

    const basicInfoFields = useMemo(() => [
        { name: 'studentName', label: 'Name', rules: [{ required: true, message: 'Please enter student name' }], colProps: { xs: 24, md: 12, lg: 8 } },
        {
            name: 'gender',
            label: 'Gender',
            type: 'select',
            rules: [{ required: true, message: 'Please select gender' }],
            options: [
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
            ],
            colProps: { xs: 24, md: 12, lg: 8 }
        }, // NEW: Gender Select
        {
            name: 'allotted_category',
            label: 'Category (Poverty Status)',
            type: 'select',
            options: [
                { value: 'General', label: 'General' },
                { value: 'SC', label: 'SC' },
                { value: 'ST', label: 'ST' },
                { value: 'OBC', label: 'OBC' },
            ],
            colProps: { xs: 24, md: 12, lg: 8 }
        },
        { name: 'dob', label: 'Date of birth', type: 'date', rules: [{ required: true, message: 'Please select date of birth' }], colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'address', label: 'Address', type: 'textarea', rules: [{ required: true, message: 'Please enter address' }], colProps: { xs: 24, md: 24 } },
        { name: 'profilePicture', label: 'Profile Photo', type: 'photo', colProps: { xs: 24 } },
    ], []);

    const gradeOptions = [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B+', label: 'B+' },
        { value: 'B', label: 'B' },
        { value: 'C+', label: 'C+' },
        { value: 'C', label: 'C' },
        { value: 'D+', label: 'D+' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
    ];

    const academicFields = useMemo(() => [
        { name: 'sslc_reg_no', label: 'SSLC Reg. No', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'sslc_school', label: 'SSLC School', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'admissionNo', label: 'Admission No', rules: [{ required: true, message: 'Please enter admission number' }], colProps: { xs: 24, md: 12, lg: 8 } },
        // rollNo shown only in edit mode
        { name: 'rollNo', label: 'Roll No.', inputType: 'number', colProps: { xs: 24, md: 12, lg: 8 }, cond: () => isEditMode },
        { name: 'classId', label: 'Class', rules: [{ required: true, message: 'Please select class' }], type: 'select', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'date_of_admission', label: 'Date of admission', type: 'date', rules: [{ required: true, message: 'Please select date of admission' }], colProps: { xs: 24, md: 12, lg: 8 } },
        {
            name: 'second_language',
            label: 'Second Language',
            type: 'select',
            options: [
                { value: 'Malayalam', label: 'Malayalam' },
                { value: 'Arabic', label: 'Arabic' },
                { value: 'Urdu', label: 'Urdu' },
                { value: 'Sanskrit', label: 'Sanskrit' },
                { value: 'Hindi', label: 'Hindi' },
                { value: 'Tamil', label: 'Tamil' },
                { value: 'Kannada', label: 'Kannada' },
                { value: 'None', label: 'None' },
            ],
            colProps: { xs: 24, md: 12, lg: 8 }
        },
    ], [isEditMode]);

    const sslcGradeFields = useMemo(() => [
        { name: 'sslc_maths', label: 'Maths', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
        { name: 'sslc_physics', label: 'Physics', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
        { name: 'sslc_chemistry', label: 'Chemistry', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
        { name: 'sslc_biology', label: 'Biology', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
        { name: 'sslc_social_studies', label: 'Social Studies', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
        { name: 'sslc_english', label: 'English', type: 'select', options: gradeOptions, colProps: { xs: 12, md: 8, lg: 4 } },
    ], [gradeOptions]);

    const familyContactFields = [
        {
            name: 'phone_number',
            label: 'Contact Phone Number',
            colProps: { xs: 24, md: 12, lg: 8 },
            rules: [
                { required: false },
                // Simple validation for 10-digit phone number (common in India)
                {
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        if (/^\d{10}$/.test(value)) return Promise.resolve();
                        return Promise.reject('Phone number must be exactly 10 digits');
                    }
                }
            ],
        }, // NEW: Phone Number
    ];

    const familyFatherFields = [
        { name: 'parentName', label: 'Name of father', rules: [{ required: true, message: 'Please enter father\'s name' }], colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'father_occupation', label: 'Occupation of father', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'father_qualification', label: 'Edu. qualification', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'father_is_alive', label: 'Father Alive/Not', type: 'select', options: [{ value: 'true', label: 'Alive' }, { value: 'false', label: 'Not Alive' }], colProps: { xs: 24, md: 12, lg: 8 } },
    ];

    const familyMotherFields = [
        { name: 'mother_name', label: 'Name of mother', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'mother_occupation', label: 'Occupation of Mother', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'mother_qualification', label: 'Edu. qualification', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'mother_is_alive', label: 'Mother Alive/Not', type: 'select', options: [{ value: 'true', label: 'Alive' }, { value: 'false', label: 'Not Alive' }], colProps: { xs: 24, md: 12, lg: 8 } },
    ];

    const guardianFields = [
        { name: 'guardian_name', label: 'Name of Guardian', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'guardian_phone_number', label: 'Phone Number of Guardian', colProps: { xs: 24, md: 12, lg: 8 } },
    ];

    const otherInfoFields = [
        { name: 'religion_caste', label: 'Religion & Caste', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'ration_card_type', label: 'Ration card (APL/BPL)', type: 'select', options: [{ value: 'APL', label: 'APL' }, { value: 'BPL', label: 'BPL' }], colProps: { xs: 24, md: 12, lg: 8 } },
        {
            name: 'aadhar_no',
            label: 'Aadhar No',
            colProps: { xs: 24, md: 12, lg: 8 },
            render: (field) => (
                <Form.Item
                    name="aadhar_no"
                    label="Aadhar No"
                    rules={[
                        { required: false },
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                if (/^\d{12}$/.test(value)) return Promise.resolve();
                                return Promise.reject('Aadhar number must be exactly 12 digits');
                            }
                        }
                    ]}
                >
                    <Input
                        maxLength={12}
                        inputMode="numeric"
                        pattern="\d*"
                        placeholder="Enter 12-digit Aadhar number"
                    />
                </Form.Item>
            )
        },
        { name: 'blood_group', label: 'Blood group', type: 'select', options: [{ value: 'A+', label: 'A+' }, { value: 'A-', label: 'A-' }, { value: 'B+', label: 'B+' }, { value: 'B-', label: 'B-' }, { value: 'O+', label: 'O+' }, { value: 'O-', label: 'O-' }, { value: 'AB+', label: 'AB+' }, { value: 'AB-', label: 'AB-' }], colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'sws_no', label: 'SWS No', colProps: { xs: 24, md: 12, lg: 8 } },
    ];

    const bankFields = [
        { name: 'bank_name', label: 'Name of Bank', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'bank_account_no', label: 'Bank A/C No', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'ifsc_code', label: 'IFSC Code', colProps: { xs: 24, md: 12, lg: 8 } },
        { name: 'scholarship_received', label: 'Scholarship Received', type: 'checkbox', valuePropName: 'checked', colProps: { xs: 24, md: 12, lg: 8 } },
    ];

    const artsFields = [
        { name: 'participated_in_arts', label: 'Participated in arts (YES/NO)', type: 'checkbox', valuePropName: 'checked', colProps: { xs: 24, md: 8 } },
        { name: 'arts_items', label: 'Items Participated', type: 'textarea', colProps: { xs: 24, md: 8 } },
        { name: 'arts_prizes', label: 'Places/Prizes obtained', type: 'textarea', colProps: { xs: 24, md: 8 } },
    ];

    const sportsFields = [
        { name: 'participated_in_sports', label: 'Participation in sports (YES/NO)', type: 'checkbox', valuePropName: 'checked', colProps: { xs: 24, md: 8 } },
        { name: 'sports_items', label: 'Items Participated', type: 'textarea', colProps: { xs: 24, md: 8 } },
        { name: 'sports_prizes', label: 'Places/Prizes obtained', type: 'textarea', colProps: { xs: 24, md: 8 } },
    ];

    const healthFields = [
        { name: 'chronic_illnesses', label: 'Any chronic patients in family', type: 'textarea', colProps: { xs: 24, md: 12 } },
        { name: 'disease', label: 'Disease', type: 'textarea', colProps: { xs: 24, md: 12 } },
    ];

    const tabStructure = useMemo(() => [
        {
            key: '1', tab: 'Basic & Academic', sections: [
                { title: 'Basic Information', fields: basicInfoFields },
                { title: 'Academic Information', fields: academicFields },
                { title: 'SSLC Grades', fields: sslcGradeFields }
            ]
        },
        // UPDATED: Added new Contact section
        { key: '2', tab: 'Family Details', sections: [{ title: 'Contact Information', fields: familyContactFields }, { title: 'Father\'s Details', fields: familyFatherFields }, { title: 'Mother\'s Details', fields: familyMotherFields }, { title: 'Guardian Details', fields: guardianFields }] },
        { key: '3', tab: 'Other Information', sections: [{ fields: otherInfoFields }, { title: 'Bank Details', fields: bankFields }] },
        { key: '4', tab: 'Extra-Curricular & Health', sections: [{ title: 'Arts', fields: artsFields }, { title: 'Sports', fields: sportsFields }, { title: 'Health', fields: healthFields }] },
    ], [basicInfoFields, academicFields, sslcGradeFields]);
    // --- END: Field Definitions ---


    // Helper: find which tab contains a field
    const findTabForField = useCallback((fieldName) => {
        for (const tab of tabStructure) {
            for (const section of tab.sections) {
                if (section.fields.some(f => f.name === fieldName)) {
                    return tab.key;
                }
            }
        }
        return null;
    }, [tabStructure]);

    // Helper: get field names for a tab (respecting conditional fields)
    const getFieldNamesByTab = useCallback((key) => {
        const tab = tabStructure.find(t => t.key === key);
        if (!tab) return [];
        const names = [];
        tab.sections.forEach(section => {
            section.fields.forEach(f => {
                if ((f.cond ? f.cond() : true) && f.name) {
                    names.push(f.name);
                }
            });
        });
        return names;
    }, [tabStructure]);

    // Validate and move to next tab (for non-last tabs)
    const handleNextForTab = async (key) => {
        const names = getFieldNamesByTab(key);
        try {
            await form.validateFields(names);
            const idx = tabStructure.findIndex(t => t.key === key);
            const next = tabStructure[idx + 1];
            if (next) setActiveTab(next.key);
        } catch (err) {
            notification.error({ message: 'Validation Error', description: 'Please fix errors before proceeding.' });
        }
    };

    // Validate entire form and submit (used by Save at last tab)
    const handleSaveAndSubmit = async () => {
        try {
            await form.validateFields();
            form.submit();
        } catch (err) {
            notification.error({ message: 'Validation Error', description: 'Please fix errors before saving.' });
        }
    };

    // --- ADD: Validate on tab change ---
    const handleTabChange = async (targetKey) => {
        const currentTab = activeTab;
        if (currentTab === targetKey) {
            setActiveTab(targetKey);
            return;
        }
        // Validate current tab fields before switching
        const names = getFieldNamesByTab(currentTab);
        try {
            await form.validateFields(names);
            setActiveTab(targetKey);
        } catch (err) {
            notification.error({ message: 'Validation Error', description: 'Please fix errors before switching tabs.' });
            // Do not switch tab
        }
    };
    // --- END ADD ---

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [classData, settingsResponse] = await Promise.all([
                    getAllClasses(),
                    api.get('/settings')
                ]);
                const settingsData = settingsResponse.data;
                setSettings(settingsData);

                let isClassTeacherOfStudent = false;

                if (isEditMode) {
                    const studentData = await getStudentById(id);
                    if (user && user.role === 'teacher' && studentData.class_id) {
                        isClassTeacherOfStudent = classData.some(c => c.id === parseInt(studentData.class_id) && c.class_teacher_id === user.id);
                    }
                    const formattedData = {
                        ...studentData,
                        dob: studentData.dob ? dayjs(studentData.dob) : null,
                        date_of_admission: studentData.date_of_admission ? dayjs(studentData.date_of_admission) : null,
                        classId: studentData.class_id ? parseInt(studentData.class_id) : null,
                        rollNo: studentData.roll_no,
                        admissionNo: studentData.admission_no,
                        studentName: studentData.student_name,
                        parentName: studentData.parent_name,
                        // NEW: Gender and Phone
                        gender: studentData.gender || null,
                        phone_number: studentData.phone_number || null,
                        father_is_alive: studentData.father_is_alive !== null ? String(studentData.father_is_alive) : null,
                        mother_is_alive: studentData.mother_is_alive !== null ? String(studentData.mother_is_alive) : null,
                        second_language: studentData.second_language || 'None',
                        sslc_maths: studentData.sslc_maths,
                        sslc_physics: studentData.sslc_physics,
                        sslc_chemistry: studentData.sslc_chemistry,
                        sslc_biology: studentData.sslc_biology,
                        sslc_social_studies: studentData.sslc_social_studies,
                        sslc_english: studentData.sslc_english,
                    };
                    form.setFieldsValue(formattedData);
                    if (studentData.profile_picture_url) {
                        setProfilePictureUrl(studentData.profile_picture_url);
                    }
                }

                setIsClassTeacher(isClassTeacherOfStudent);

                // Filter classes list if teacher AND setting is false
                if (user && user.role === 'teacher' && settingsData?.allow_teacher_student_edit === false) {
                    const teacherClasses = classData.filter(c => c.class_teacher_id === user.id);
                    setClasses(teacherClasses);
                } else {
                    setClasses(classData);
                }
            } catch (error) {
                console.error(error);
                message.error('Failed to load initial data.');
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [id, form, isEditMode, user, message]);

    // --- RENDER FIELD FUNCTION ---
    const renderField = useCallback((field) => {
        if (field.render) {
            return (
                <Col {...(field.colProps || { xs: 24, md: 12, lg: 8 })} key={field.name}>
                    {field.render(field)}
                </Col>
            );
        }

        const common = {
            name: field.name,
            label: field.label,
            rules: field.rules || [],
            valuePropName: field.valuePropName || undefined,
        };
        const colProps = field.colProps || { xs: 24, md: 12, lg: 8 };

        let input = null;
        switch (field.type) {
            case 'photo':
                const handlePhotoChange = ({ file }) => {
                    // When a file is removed, file.status is 'removed'.
                    // When a new file is selected, we get the file object.
                    if (file.status === 'removed') {
                        setProfilePicture(null);
                    } else {
                        setProfilePicture(file.originFileObj || file);
                    }
                };

                return (
                    <Col {...colProps} key={field.name}>
                        <Form.Item label={field.label}>
                            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                <Avatar
                                    size={120}
                                    icon={<CameraOutlined />}
                                    src={
                                        profilePicture
                                            ? URL.createObjectURL(profilePicture)
                                            : profilePictureUrl
                                                ? `${process.env.REACT_APP_API_URL || ''}${profilePictureUrl}`
                                                : undefined
                                    }
                                    style={{
                                        backgroundColor: token.colorPrimary,
                                        marginBottom: 8,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                />
                            </div>
                            <ImgCrop rotationSlider showReset quality={1} aspect={3.5 / 4.5} modalTitle="Crop Student Photo" modalOk="Confirm Crop">
                                <Upload
                                    accept="image/*"
                                    maxCount={1}
                                    fileList={profilePicture ? [{ uid: '-1', name: 'photo.png', status: 'done', url: URL.createObjectURL(profilePicture), originFileObj: profilePicture }] : []}
                                    beforeUpload={(file) => {
                                        setProfilePicture(file);
                                        return false;
                                    }}
                                    onRemove={() => setProfilePicture(null)}
                                >
                                    <Button icon={<CameraOutlined />} block>
                                        {profilePicture || profilePictureUrl ? 'Change Photo' : 'Upload Photo'}
                                    </Button>
                                </Upload>
                            </ImgCrop>
                        </Form.Item>
                    </Col>
                );
            case 'date':
                if (field.name === 'dob') {
                    input = (
                        <DatePicker
                            style={{ width: '100%' }}
                            disabledDate={date => date && date > dayjs().subtract(5, 'year')}
                        />
                    );
                } else {
                    input = <DatePicker style={{ width: '100%' }} />;
                }
                break;
            case 'select':
                if (field.name === 'classId') {
                    input = (
                        <Select placeholder={field.placeholder || 'Select a class'}>
                            {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                        </Select>
                    );
                } else {
                    input = (
                        <Select>
                            {(field.options || []).map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                        </Select>
                    );
                }
                break;
            case 'textarea':
                input = <Input.TextArea 
                    rows={field.rows || 2} 
                    style={['address'].includes(field.name) ? { textTransform: 'uppercase' } : {}}
                    onInput={['address'].includes(field.name) ? (e) => { e.target.value = e.target.value.toUpperCase(); } : undefined}
                />;
                break;
            case 'checkbox':
                input = <Checkbox />;
                break;
            default:
                if (
                    ['admissionNo', 'rollNo', 'sslc_reg_no', 'bank_account_no', 'sws_no'].includes(field.name)
                ) {
                    input = (
                        <Input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={field.maxLength || undefined}
                            placeholder={field.placeholder}
                        />
                    );
                } else if (field.name === 'phone_number') { // NEW: Phone number input
                    input = (
                        <Input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={10}
                            placeholder="Enter 10-digit phone number"
                        />
                    );
                } else {
                    input = <Input 
                        type={field.inputType || 'text'} 
                        style={['studentName', 'parentName', 'mother_name', 'guardian_name'].includes(field.name) ? { textTransform: 'uppercase' } : {}}
                        onInput={['studentName', 'parentName', 'mother_name', 'guardian_name'].includes(field.name) ? (e) => { e.target.value = e.target.value.toUpperCase(); } : undefined}
                    />;
                }
        }

        return (
            <Col {...colProps} key={field.name}>
                <Form.Item {...common}>
                    {input}
                </Form.Item>
            </Col>
        );
    }, [classes, profilePicture, profilePictureUrl, token]);
    // --- END RENDER FIELD FUNCTION ---

    const renderTabNavigation = useCallback((key) => {
        const isLastTab = key === tabStructure[tabStructure.length - 1].key;

        // Determine button styles for primary buttons
        const primaryButtonStyle = {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: '#fff',
            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.045)',
        };


        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 24,
                    padding: isMobile ? '0 8px' : '0',
                    flexWrap: 'wrap',
                    gap: 8,
                    width: '100%'
                }}
            >
                <Space style={{ width: '100%', justifyContent: isMobile ? 'space-around' : 'flex-end' }}>
                    {!isLastTab && (
                        <Button
                            type="default"
                            onClick={() => handleNextForTab(key)}
                            block={isMobile}
                            style={primaryButtonStyle}
                        >
                            Next
                        </Button>
                    )}
                    {isLastTab && (
                        <Button
                            type="default"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSaveAndSubmit}
                            block={isMobile}
                            style={primaryButtonStyle}
                        >
                            Save
                        </Button>
                    )}
                </Space>
            </div>
        );
    }, [tabStructure, isMobile, saving, token.colorPrimary]); // Added token.colorPrimary to dependencies

    const onFinish = (values) => {
        setSaving(true);
        const payload = {
            ...values,
            dob: values.dob?.format('YYYY-MM-DD'),
            date_of_admission: values.date_of_admission?.format('YYYY-MM-DD'),
            roll_no: values.rollNo,
            admissionNo: values.admissionNo,
            studentName: values.studentName,
            parentName: values.parentName,
            // NEW: Include phone_number and gender in the payload
            phone_number: values.phone_number || null,
            gender: values.gender || null,
            second_language: values.second_language || 'None',
        };

        const saveAction = isEditMode ? updateStudent(id, payload) : createStudent(payload);

        (async () => {
            try {
                const response = await saveAction;
                // If profile picture is selected, upload it
                if (profilePicture) {
                    setUploadingPicture(true);
                    const studentId = isEditMode ? id : response.id;
                    await uploadStudentProfilePicture(studentId, profilePicture);
                    setUploadingPicture(false);
                    notification.success({
                        message: 'Success',
                        description: `Student ${isEditMode ? 'updated' : 'created'} successfully with profile picture!`,
                    });
                    navigate('/students');
                } else {
                    notification.success({
                        message: 'Success',
                        description: `Student ${isEditMode ? 'updated' : 'created'} successfully!`,
                    });
                    navigate('/students');
                }
            } catch (error) {
                setUploadingPicture(false);
                const errorData = error.response?.data;

                // Handle field-mapped errors from backend
                if (errorData && (errorData.fields || errorData.details)) {
                    const fieldErrors = [];

                    // Handle new field-mapped format
                    if (errorData.fields) {
                        Object.entries(errorData.fields).forEach(([field, message]) => {
                            fieldErrors.push({ name: field, errors: [message] });
                        });
                    }

                    // Handle array format (fallback/legacy)
                    if (errorData.details && Array.isArray(errorData.details)) {
                        errorData.details.forEach(err => {
                            if (err.path && !fieldErrors.some(fe => fe.name === err.path)) {
                                fieldErrors.push({ name: err.path, errors: [err.msg] });
                            } else if (err.param && !fieldErrors.some(fe => fe.name === err.param)) {
                                // express-validator legacy format
                                fieldErrors.push({ name: err.param, errors: [err.msg] });
                            }
                        });
                    }

                    if (fieldErrors.length > 0) {
                        form.setFields(fieldErrors);

                        // Find first error field and switch to that tab
                        const firstErrorField = fieldErrors[0].name;
                        const errorTab = findTabForField(firstErrorField);
                        if (errorTab) {
                            setActiveTab(errorTab);
                        }

                        notification.error({
                            message: 'Validation Error',
                            description: `Please fix the ${fieldErrors.length} error(s) highlighted in the form.`
                        });
                        // Stop execution here as we've handled the errors
                        return;
                    }
                }

                const backendError = errorData?.error || error.message || '';
                if (
                    backendError.toLowerCase().includes('admission number') &&
                    backendError.toLowerCase().includes('already exists')
                ) {
                    form.setFields([
                        {
                            name: 'admissionNo',
                            errors: [backendError],
                        },
                    ]);
                    // Navigate to Basic Info tab where admissionNo is usually located
                    const tab = findTabForField('admissionNo');
                    if (tab) setActiveTab(tab);
                } else {
                    // Generic error notification
                    notification.error({
                        message: 'Error',
                        description: backendError || `Failed to ${isEditMode ? 'update' : 'create'} student.`
                    });
                }
            } finally {
                setSaving(false);
            }
        })();
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;
    }

    if (isEditDisabled) {
        return (
            <div style={{ padding: '24px' }}>
                <Title level={2} style={{ marginBottom: 16 }}>
                    {isEditMode ? 'Edit Student Details' : 'New Student Admission'}
                </Title>
                <Alert
                    message="Access Denied"
                    description="You do not have permission to add or modify student details. This feature has been disabled by the administrator."
                    type="error"
                    showIcon
                    style={{ borderRadius: 12, marginBottom: 16 }}
                />
                <Link to="/students">
                    <Button icon={<ArrowLeftOutlined />}>Back to Students</Button>
                </Link>
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                    gap: 16,
                }}
            >
                <Title level={2} style={{ margin: 0, minWidth: 220 }}>
                    {isEditMode ? 'Edit Student Details' : 'New Student Admission'}
                </Title>
                <Space wrap>
                    <Link to="/students">
                        <Button icon={<ArrowLeftOutlined />}>Cancel</Button>
                    </Link>
                    <Button htmlType="submit" loading={saving || uploadingPicture} type="primary" icon={<SaveOutlined />} style={{ borderRadius: '8px', fontWeight: 600 }}>
                        {uploadingPicture ? 'Uploading...' : (isEditMode ? 'Update Profile' : 'Complete Admission')}
                    </Button>
                </Space>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                tabBarGutter={16}
                type="line"
                style={{ marginBottom: 16 }}
            >
                {tabStructure.map(tab => (
                    <TabPane tab={tab.tab} key={tab.key}>
                        <Card bodyStyle={{ padding: '16px 8px' }} bordered={false}>
                            {tab.sections.map((sec, idx) => (
                                <div key={idx}>
                                    {sec.title && <Title level={4} style={{ fontSize: 18 }}>{sec.title}</Title>}
                                    <Row gutter={[16, 16]}>
                                        {sec.fields.filter(f => (f.cond ? f.cond() : true)).map(renderField)}
                                    </Row>
                                    {idx < tab.sections.length - 1 && <Divider />}
                                </div>
                            ))}
                        </Card>

                        {/* CRITICAL FIX: Render navigation immediately after the card inside the tab */}
                        {renderTabNavigation(tab.key)}

                    </TabPane>
                ))}
            </Tabs>
        </Form>
    );
};

export default StudentFormPage;